<script>
import Lottie from "@/components/widgets/lottie.vue";
import animationData from "@/components/widgets/tqywkdcz.json";
import { getDeviceFingerprint } from "@/libs/fingerprint";
import { authComputed } from '@/state/helpers'

export default {
  name: "TwoFactorVerificationModal",
  components: { Lottie },

  data() {
    return {
      digits: ["", "", "", "", "", ""], // 6 boxes
      loading: false,
      step: "verify", // verify | success | recovery
      lottieOptions: { animationData },
      verificationId: null,
      tempToken: null,
      successTimeout: null,
      trustDevice: false,
      recoveryCode: "",
    };
  },

  computed: {
    ...authComputed,
    verificationCode() {
      return this.digits.join("");
    },
    isLoggedIn() {
      return this.loggedIn;
    },
    verifyApi() {
      return this.isLoggedIn
        ? '/service/centralize-authenticate/api/tenant/v1/security/verify'
        : '/service/centralize-authenticate/api/tenant/v1/security/2fa/login/verify';
    },
  },

  watch: {
    recoveryCode(val) {
      this.recoveryCode = val.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    }
  },

  methods: {
    openModal(data = null) {
      this.resetState();
      this.verificationId = data?.verification_id || null;
      this.tempToken = data?.temp_token || '';
      this.$refs.twoFactorVerificationModal.show();
    },

    closeModal() {
      this.resetState();
      clearTimeout(this.successTimeout);
      this.$emit("closed");
      // Notify axios interceptor if cancelled
      window.dispatchEvent(
        new CustomEvent("2fa-verification-completed", {
          detail: { success: false, cancelled: true },
        })
      );
      this.$refs.twoFactorVerificationModal.hide();
    },

    resetState() {
      this.step = "verify";
      this.digits = ["", "", "", "", "", ""];
      this.loading = false;
      this.verificationId = null;
      this.tempToken = null;
      this.trustDevice = false;
      this.recoveryCode = "";
      clearTimeout(this.successTimeout);
    },

    focusInput(index) {
      this.$nextTick(() => {
        const el = this.$refs[`digit${index}`]?.[0];
        if (el) setTimeout(() => el.focus(), 10);
      });
    },

    onKeyDown(index, event) {
      const key = event.key;

      if (key === "Backspace") {
        if (this.digits[index]) {
          this.digits[index] = "";
        } else if (index > 0) {
          const prevIndex = index - 1;
          this.digits[prevIndex] = "";
          this.$nextTick(() => {
            const prev = document.getElementById(`digit${prevIndex + 1}-input`);
            if (prev) prev.focus();
          });
        }
        event.preventDefault(); // prevent weird jumps
      } else if (/^\d$/.test(key)) {
        this.digits[index] = key;
        if (index < this.digits.length - 1) {
          this.$nextTick(() => {
            const next = document.getElementById(`digit${index + 2}-input`);
            if (next) next.focus();
          });
        }
        event.preventDefault();
      }  else if (key === "Enter") {
        // Press Enter to verify (anytime)
        if (this.verificationCode.length === 6) {
          this.verifyCode();
        }
        event.preventDefault();
      }
    },

    onPaste(event) {
      event.preventDefault();

      const pastedData = event.clipboardData.getData('text');
      const pastedCode = pastedData.replace(/\D/g, '');

      if (pastedCode.length > 0) {
        for (let i = 0; i < Math.min(pastedCode.length, this.digits.length); i++) {
          this.digits[i] = pastedCode[i];
        }

        const nextIndex = Math.min(pastedCode.length, this.digits.length - 1);
        const nextInput = document.getElementById(`digit${nextIndex + 1}-input`);
        if (nextInput) {
          nextInput.focus();
        }

        if (pastedCode.length >= 6) {
          setTimeout(() => this.verifyCode(), 100);
        }
      }
    },

    async verifyCode() {
      if (this.loading) return;

      if (!/^\d{6}$/.test(this.verificationCode)) {
        return this.toastError("Error", this.$t("t-enter-invalid-code"));
      }

      this.loading = true;

      try {
        const deviceFingerprint = await getDeviceFingerprint();

        const response = await this.$axios.post(this.verifyApi,
          {
            code: this.verificationCode,
            device_fingerprint: deviceFingerprint,
            security_verification_id: this.verificationId,
            trust_device: this.trustDevice
          },
          {
            headers: {
              'X-Temp-Token': this.tempToken
            }
          }
        );

        if (response.data.status) {
          this.step = "success";
          this.toastSuccess("Success", response.data.message);
          // Notify axios interceptor if verified
          window.dispatchEvent(
            new CustomEvent("2fa-verification-completed", {
              detail: {
                success: true,
                security_verification_id: response.data.data?.security_verification_id || null
              },
            })
          );
          // Slight delay for user feedback before closing
          this.successTimeout = setTimeout(() => {
            this.$emit("completed");
            this.$refs.twoFactorVerificationModal.hide();
          }, 1200);
        } else {
          this.toastError("Error", response.data.message);
        }
      } catch (error) {
        this.errorHandler(error);
      } finally {
        this.loading = false;
      }
    },
    async verifyRecoveryCode() {
      if (this.loading) return;

      if (this.recoveryCode.length < 8) {
        return this.toastError("Error", this.$t("t-enter-invalid-recovery-code"));
      }

      this.loading = true;

      try {
        const response = await this.$axios.post(this.verifyApi,
          {
            recovery_code: this.recoveryCode,
            security_verification_id: this.verificationId,
          },
          {
            headers: { 'X-Temp-Token': this.tempToken }
          }
        );

        if (response.data.status) {
          this.step = "success";
          this.toastSuccess("Success", response.data.message);
          // Notify axios interceptor if verified
          window.dispatchEvent(
            new CustomEvent("2fa-verification-completed", {
              detail: {
                success: true,
                security_verification_id: response.data.data?.security_verification_id || null
              },
            })
          );
          // Slight delay for user feedback before closing
          this.successTimeout = setTimeout(() => {
            this.$emit("completed");
            this.$refs.twoFactorVerificationModal.hide();
          }, 1200);
        } else {
            this.toastError("Error", response.data.message);
        }
      } catch (error) {
          this.errorHandler(error);
      } finally {
          this.loading = false;
      }
    },
  },

  /*mounted() {
    this._listener = (e) => this.openModal(e.detail);
    window.addEventListener("show-2fa-verification", this._listener);
  },

  beforeUnmount() {
    window.removeEventListener("show-2fa-verification", this._listener);
  },*/
};
</script>

<template>
  <BModal
    ref="twoFactorVerificationModal"
    centered
    modal-class="flip"
    class="v-modal-custom"
    hide-footer
    header-class="border-bottom pb-3"
    no-close-on-backdrop
    no-close-on-esc
    hide-header-close
    @shown="focusInput(0)"
    style="z-index: 2000"
  >
    <template #header>
      <div class="d-flex align-items-center gap-2">
        <i class="mdi mdi-shield-key-outline fs-4 text-primary"></i>
        <h5 class="mb-0 fw-semibold text-capitalize">{{ $t("t-method-verification", { method: "Two Factor"}) }}</h5>
      </div>
    </template>

    <!-- Step: Verify -->
    <div v-if="step === 'verify'" class="card mt-3 border-0 shadow-sm rounded-3">
      <div class="card-body p-4">
        <div class="mb-3 text-center">
          <div class="avatar-lg mx-auto">
            <div
                class="avatar-title bg-light text-primary display-5 rounded-circle"
            >
              <i class="ri-lock-2-line"></i>
            </div>
          </div>
        </div>

        <div class="p-2 mt-3">
          <div class="text-muted text-center mb-3 mx-lg-3">
            <h4 class="text-capitalize mb-1">
              {{ $t("t-verify-your-identity") }}
            </h4>
            <p class="mb-0">{{ $t("t-enter-code-from-app") }}</p>
            <small class="text-warning fw-semibold">
              <i class="ri-time-line me-2 fs-5 text-warning"></i>
              <span>{{ $t('t-please-verify-within-mins', { minutes: 10 }) }}</span>
            </small>
          </div>

          <!-- OTP input boxes -->
          <form autocomplete="off">
            <div class="row justify-content-center">
              <div
                  v-for="(digit, index) in digits"
                  :key="index"
                  class="col-2 px-1"
              >
                <input
                  type="text"
                  class="form-control form-control-lg bg-light border-light text-center fw-semibold"
                  maxlength="1"
                  :ref="'digit' + index"
                  v-model="digits[index]"
                  @keydown="onKeyDown(index, $event)"
                  @paste="onPaste($event)"
                  :disabled="loading"
                  :id="`digit${index + 1}-input`"
                />
              </div>
            </div>
          </form>

          <!-- Trust this device -->
          <div class="mt-4 text-start d-flex align-items-start gap-2">
            <input
              class="form-check-input mt-1"
              type="checkbox"
              id="trustDevice2fa"
              v-model="trustDevice"
              :disabled="loading"
            />
            <label class="form-check-label small text-muted" for="trustDevice2fa">
              <span class="fw-semibold text-body">{{ $t('t-trust-device') }}</span><br />
              <small>{{ $t('t-trust-device-desc') }}</small>
            </label>
          </div>

          <div class="mt-4 d-flex gap-2">
            <BButton
              variant="outline-secondary"
              class="w-50"
              @click="closeModal"
              :disabled="loading"
            >
              <i class="mdi mdi-close fs-5"></i> {{ $t("t-cancel") }}
            </BButton>
            <BButton
              variant="success"
              class="w-50"
              @click="verifyCode"
              :disabled="loading || verificationCode.length < 6"
            >
              <i class="mdi mdi-lock-check-outline fs-5"></i>
              {{ loading ? $t("t-verifying") + "..." : $t("t-verify") }}
            </BButton>
          </div>

          <!-- Recover verification code -->
          <div class="text-center mt-3">
            <small class="text-muted">
              {{ $t('t-lost-verification-code') }}
              <a
                href="javascript:void(0)"
                class="fw-semibold text-decoration-underline"
                @click="step = 'recovery'"
              >
                {{ $t('t-use-recovery-code') }}
              </a>
            </small>
          </div>
        </div>
      </div>
    </div>

    <!-- Step: Success -->
    <div v-else-if="step === 'success'" class="text-center py-4">
      <div class="mb-4">
        <Lottie
          :options="lottieOptions"
          colors="primary:#121331,secondary:#08a88a"
          trigger="loop"
          :height="120"
          :width="120"
        />
      </div>
      <h5 class="text-success fw-semibold text-capitalize">
        {{ $t("t-verified-successfully") }}
      </h5>
      <p class="text-muted small mb-0 fs-6"> {{ $t("t-loading") }}<span class="loading-dots"></span></p>
    </div>

    <!-- Step: Recovery Code -->
    <div v-else-if="step === 'recovery'" class="card mt-3 border-0 shadow-sm rounded-3">
      <div class="card-body p-4">
        <div class="mb-3 text-center">
          <div class="avatar-lg mx-auto">
            <div class="avatar-title bg-light text-primary display-5 rounded-circle">
              <i class="mdi mdi-key-outline"></i>
            </div>
          </div>
        </div>

        <div class="text-muted text-center mb-3 mx-lg-3">
          <h4 class="text-capitalize mb-1">
            {{ $t("t-verify-your-identity") }}
          </h4>
          <p class="mb-0">{{ $t('t-enter-recovery-verify-identity') }}</p>
          <small class="text-warning fw-semibold">
            <i class="mdi mdi-repeat-off me-2 fs-5 text-warning"></i>
            <span>{{ $t('t-recovery-code-once') }}</span>
          </small>
        </div>

        <input
          type="text"
          class="form-control form-control-md bg-light border-light text-center fw-semibold"
          maxlength="20"
          v-model="recoveryCode"
          :disabled="loading"
          id="recoveryCode"
          :placeholder="$t('t-recovery-code')"
        />

        <div class="mt-4 d-flex gap-2">
          <BButton
            variant="success"
            class="w-100"
            @click="verifyRecoveryCode"
            :disabled="loading || recoveryCode.length < 8"
          >
            <i class="ri-verified-badge-line fs-5"></i>
            {{ loading ? $t("t-verifying") + "..." : $t("t-verify") }}
          </BButton>
        </div>

        <!-- Back to verification code page -->
        <div class="text-center mt-3">
          <a
            href="javascript:void(0)"
            class="fw-semibold ms-2"
            @click="step = 'verify'"
          >
            {{ $t('t-back-verification-code') }}
          </a>
        </div>
      </div>
    </div>

  </BModal>
</template>

<style scoped>
.v-modal-custom {
  z-index: 99999 !important;
}

.modal-backdrop {
  z-index: 99998 !important;
}

.avatar-lg {
  width: 80px;
  height: 80px;
}

.avatar-title {
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
}

input.form-control-lg {
  font-size: 1.5rem;
}

input.form-control-lg:focus {
  border-color: var(--bs-success);
  box-shadow: 0 0 0 0.2rem rgba(25, 135, 84, 0.25);
}

.loading-dots::after {
  content: '';
  display: inline-block;
  animation: dots 1s steps(5, end) infinite;
}

@keyframes dots {
  0%   { content: ''; }
  20%  { content: '.'; }
  40%  { content: '..'; }
  60%  { content: '...'; }
  80%  { content: '....'; }
  100% { content: ''; }
}
</style>
